import { css, cx } from "@emotion/css";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	generatePath,
	Link as LinkRouter,
	useLocation,
} from "react-router-dom";
import routing from "routing";
import { CustomBadge } from "src/components/Default/CustomBadge";
import { TextBlackButton } from "src/components/Default/LekUI/Button";
import { FolderColorIcon } from "src/components/Folders/FolderColorIcon";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { useFoldersSearch } from "src/hooks/useFolders";
import PlusIcon from "src/images/Icons/newIcons/plus.svg";
import { Link } from "./Link";

interface NavbarFoldersProps {
	open: boolean;
}

export const NavbarFolders = ({ open }: NavbarFoldersProps) => {
	const { t } = useTranslation();
	const location = useLocation();
	const { openModal } = useModals();
	const { data: foldersData, isLoading } = useFoldersSearch({
		take: 10,
		page: 0,
	});

	if (isLoading) {
		return (
			<Box className="flex justify-center py-4">
				<CircularProgress size={24} />
			</Box>
		);
	}

	const folders = foldersData?.data || [];
	const totalCount = foldersData?.totalCount;

	return (
		<Box className="flex flex-col">
			<Box className="px-5 py-2">
				<Divider />
			</Box>
			{open && (
				<Box className="px-3 pb-2">
					<TextBlackButton
						component={LinkRouter}
						to={generatePath(routing.paths.foldersList)}
						className="w-full justify-between text-left px-2"
						data-test-id="nav-your-folders-link"
					>
						{t("navbar.yourFolders", "Twoje foldery")}
						{totalCount != null && ` (${totalCount})`}
						<ChevronRight className="icon-small" />
					</TextBlackButton>
				</Box>
			)}
			<List className="flex flex-col gap-4">
				{folders.map((folder) => {
					const href = generatePath(routing.paths.folder, { id: folder.id });
					const isSelected = location.pathname.startsWith(href);

					return (
						<Link
							key={folder.id}
							href={href}
							Icon={
								<FolderColorIcon
									color={folder.details?.color}
									iconClassName="icon-small"
									className="folder-color-label-icon--compact square-32"
								/>
							}
							selected={isSelected}
							data-test-id="nav-folders-link"
							data-folder-id={folder.id}
							className={cx("drawer--menu--item size-18-light", {
								"drawer--menu--item-short": !open,
							}, !open && css`padding-left: 7px;`)}
						>
							{open && (
								<span className="flex items-center justify-between w-100 min-w-0">
									<span className="text-ellipsis-1 min-w-0">{folder.name}</span>
									<CustomBadge
										count={folder.totalItemsCount}
										maxCount={99}
										className="ml-8"
									/>
								</span>
							)}
						</Link>
					);
				})}
				<Link
					className="drawer--menu--item size-18-light"
					onClick={() => openModal("CreateFolderModal")}
					Icon={<PlusIcon className="icon-big" />}
					data-test-id="navbar-add-folder-button"
				>
					{open && t("navbar.addFolder")}
				</Link>
			</List>
		</Box>
	);
};
